import React from "react";
import { LoadingIndicator, ErrorMessage, Button, Text } from "@jsq/ds/core";
import { stackedLg } from "../../../../common/themes/spacing_styles_vanilla.css";
import { useGetAccountNameQuery } from "../../../../graphql/supergraph.index";
import { WorkflowType, CreatePaymentPrefEntryPoint, WorkflowMode } from "../../common/enums";
import DeletePaymentPrefModal from "../../Components/DeletePaymentPrefModal";
import { useDeletePaymentPref } from "../../Components/DeletePaymentPrefModal/hooks/use-delete-payment-pref";
import { ExistingPaymentPrefDisplayCard } from "../../Components/ExistingPaymentPrefDisplayCard";
import { PaymentPrefForPositionWorkflow } from "../../Workflows/PaymentPrefForPositionWorkflow";
import {
  CreateWorkflowParams,
  EditWorkflowParams,
} from "../../Workflows/PaymentPrefForPositionWorkflow/types";
import { positionPaymentPrefVar } from "./apollo-vars";
import { usePositionPaymentPref } from "./hooks/use-position-payment-pref";
import { PaymentPrefForPositionContainerProps } from "./types";

/**
 * A container component that manages and displays payment preferences for a specific position.
 * It handles the creation, editing, and deletion of payment preferences, as well as displaying
 * existing payment preferences and their details.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {string} props.accountId - ID of the account associated with the position
 * @param {string} props.entityId - ID of the entity associated with the position
 * @param {string} props.entityName - Name of the entity associated with the position
 * @param {string} props.positionId - ID of the position to manage payment preferences for
 * @param {string} [props.paymentPrefId] - Optional ID of an existing payment preference
 * @param {Arena} [props.arena] - Arena configuration object that determines which mutation to call based on hasPositionPaymentPrefStaffApprovals feature flag
 *
 * @example
 * ```tsx
 * <PaymentPrefForPositionContainer
 *   accountId="123"
 *   entityId="456"
 *   entityName="Test Entity"
 *   positionId="789"
 *   paymentPrefId="101112"
 *   arena={{
 *     features: {
 *       hasPositionPaymentPrefStaffApprovals: true
 *     }
 *   }}
 * />
 * ```
 *
 * @remarks
 * - Uses usePositionPaymentPref hook for state management and business logic
 * - Uses useDeletePaymentPref hook for deletion functionality
 * - Handles loading and error states with appropriate UI feedback
 * - Supports both create and edit workflows through PaymentPrefForPositionWorkflow
 * - Displays existing payment preferences through ExistingPaymentPrefDisplayCard
 * - Provides delete functionality through DeletePaymentPrefModal
 * - Conditionally fetches account name based on arena features
 */
export const PaymentPrefForPositionContainer: React.FunctionComponent<
  PaymentPrefForPositionContainerProps
> = ({ accountId, entityId, entityName, positionId, paymentPrefId, arena }) => {
  const {
    showWorkflowComponent,
    workflowMode,
    workflowParams,
    paymentPref,
    canEdit,
    canReveal,
    loading,
    error,
    setShowWorkflowComponent,
    handlePaymentPrefCallbacks,
    paymentPrefId: displayPaymentPrefId,
  } = usePositionPaymentPref({
    accountId,
    entityId,
    entityName,
    positionId,
    paymentPrefId,
  });

  const {
    handleDeletePaymentInstructionClick,
    showDeletePaymentPrefModal,
    handleCancelDeletePaymentPref,
    deletePaymentPrefCallback,
  } = useDeletePaymentPref({
    workflowType: WorkflowType.Position,
    positionId,
    onSuccess: ({ workflowType }) => {
      if (workflowType === WorkflowType.Position) {
        // Update reactive variable to propagate query refresh
        positionPaymentPrefVar(null);
      }
    },
  });

  const { data: accountNameData, loading: accountNameLoading } = useGetAccountNameQuery({
    variables: { accountId },
    skip: !accountId || !arena?.features?.hasPositionPaymentPrefStaffApprovals,
  });

  if (loading || accountNameLoading) {
    return <LoadingIndicator variant="primary" />;
  }

  if (error) {
    return <ErrorMessage>Failed to load</ErrorMessage>;
  }

  return (
    <>
      {showWorkflowComponent && workflowMode && workflowParams && (
        <>
          {workflowMode === WorkflowMode.Create && (
            <PaymentPrefForPositionWorkflow
              onExit={() => setShowWorkflowComponent(false)}
              onSuccess={() => setShowWorkflowComponent(false)}
              workflowMode={WorkflowMode.Create}
              workflowParams={workflowParams as CreateWorkflowParams}
              accountName={accountNameData?.account?.name}
              arena={arena}
            />
          )}
          {workflowMode === WorkflowMode.Edit && (
            <PaymentPrefForPositionWorkflow
              onExit={() => setShowWorkflowComponent(false)}
              onSuccess={() => setShowWorkflowComponent(false)}
              workflowMode={WorkflowMode.Edit}
              workflowParams={workflowParams as EditWorkflowParams}
              accountName={accountNameData?.account?.name}
              arena={arena}
            />
          )}
        </>
      )}
      <DeletePaymentPrefModal
        show={showDeletePaymentPrefModal}
        onClose={handleCancelDeletePaymentPref}
        deleteCallback={deletePaymentPrefCallback}
      />
      <Text variant="h3" className={stackedLg}>
        Payment instructions
      </Text>
      {paymentPref ? (
        <ExistingPaymentPrefDisplayCard
          key={paymentPref.id}
          paymentPref={paymentPref}
          displayPositions
          displayRevealDetails={canReveal}
          displayInvalidInstructionsWarning
          callBacks={
            canEdit
              ? {
                  editPaymentInstructionsCallback: () =>
                    handlePaymentPrefCallbacks({ workflowMode: WorkflowMode.Edit }),
                  switchPaymentMethodCallback: () =>
                    handlePaymentPrefCallbacks({
                      workflowMode: WorkflowMode.Create,
                      createEntryPoint: CreatePaymentPrefEntryPoint.Switch,
                    }),
                  deletePaymentInstructionsCallback: () =>
                    displayPaymentPrefId &&
                    handleDeletePaymentInstructionClick(displayPaymentPrefId),
                }
              : undefined
          }
        />
      ) : (
        <div>
          <p>This position does not have any payment instructions configured</p>
          {canEdit && (
            <Button
              variant="neutral"
              isFullWidth
              onClick={() =>
                handlePaymentPrefCallbacks({
                  workflowMode: WorkflowMode.Create,
                  createEntryPoint: CreatePaymentPrefEntryPoint.Add,
                })
              }
            >
              Add payment instructions
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export type { PaymentPrefForPositionContainerProps };
